import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { driver } from 'driver.js';
import type { DriveStep, PopoverDOM } from 'driver.js';
import 'driver.js/dist/driver.css';
import {
  ArrowDown,
  BarChart2,
  Bot,
  ClipboardList,
  MessageSquare,
  Users,
} from 'lucide-react';
import { Button } from './ui/button/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import onboardingData from '../data/onboarding/data.json';
import { useTutorialStore } from '../stores/tutorialStore';
import type {
  OnboardingMilestone,
  OnboardingTourKey,
  OnboardingTransitionKey,
} from '../stores/tutorialStore';
import { useAuthStore } from '../stores/authStore';
import { useSimulationStore } from '../stores/simulationStore';

type OnboardingStep = {
  selector: string;
  title: string;
  description: string;
};

type OnboardingTransition = OnboardingStep & {
  key: OnboardingTransitionKey;
  sourcePath: string;
  targetPath: string;
  milestone: OnboardingMilestone;
  afterTour?: OnboardingTourKey;
};

type OnboardingActionPauseStage = OnboardingStep & {
  stage: string;
  hint?: string;
};

type OnboardingActionPause = OnboardingStep & {
  key: string;
  sourcePath: string;
  milestone: OnboardingMilestone;
  afterTour?: OnboardingTourKey;
  afterMilestones?: OnboardingMilestone[];
  hint?: string;
  /**
   * Optional sub-steps. The pause's own element carries a
   * data-onboarding-stage attribute naming the current stage, and the
   * matching entry's selector and copy are shown instead of the pause's.
   */
  stages?: OnboardingActionPauseStage[];
};

type QuitOnboardingReturnPoint =
  | { kind: 'intro' }
  | { kind: 'tour'; tourKey: OnboardingTourKey; stepIndex: number }
  | { kind: 'transition' }
  | { kind: 'action' }
  | { kind: 'wait' };

const FLOW_ICONS = [Bot, Users, ClipboardList, MessageSquare, BarChart2];
const TRANSITION_PULSE_CLASS = 'promptsafe-tour-tab-pulse';
const TRANSITION_TARGET_CLASS_PREFIX = 'promptsafe-tour-target-';
const TRANSITION_INTERACTION_CLASS = 'promptsafe-tour-transition';
const INTERACTIVE_PAUSE_CLASS = 'promptsafe-tour-interactive-pause';
const FINAL_ONBOARDING_TOUR: OnboardingTourKey = 'connections';

const routeTourMap: Record<string, OnboardingTourKey> = {
  '/agent-builder': 'agent-builder',
  '/persona-builder': 'persona-builder',
  '/evaluator-builder': 'evaluator-builder',
  '/conversation-simulation': 'conversation-simulation',
  '/evaluations': 'evaluations',
  '/settings': 'settings',
  '/connections': 'connections',
};

const onboardingTransitions: OnboardingTransition[] = [
  {
    key: 'agent-to-persona',
    sourcePath: '/agent-builder',
    targetPath: '/persona-builder',
    milestone: 'agentConfigured',
    ...onboardingData.transitions.agentToPersona,
  },
  {
    key: 'persona-to-evaluator',
    sourcePath: '/persona-builder',
    targetPath: '/evaluator-builder',
    milestone: 'personaConfigured',
    ...onboardingData.transitions.personaToEvaluator,
  },
  {
    key: 'evaluator-to-simulation',
    sourcePath: '/evaluator-builder',
    targetPath: '/conversation-simulation',
    milestone: 'evaluatorConfigured',
    ...onboardingData.transitions.evaluatorToSimulation,
  },
  {
    key: 'simulation-to-evaluations',
    sourcePath: '/conversation-simulation',
    targetPath: '/evaluations',
    milestone: 'simulationRun',
    afterTour: 'conversation-result',
    ...onboardingData.transitions.simulationToEvaluations,
  },
  // After the loop: where tokens are bought, then connect your own agent.
  {
    key: 'evaluations-to-settings',
    sourcePath: '/evaluations',
    targetPath: '/settings',
    milestone: 'evaluationScoreOpened',
    afterTour: 'closing',
    ...onboardingData.transitions.evaluationsToSettings,
  },
  {
    key: 'settings-to-connections',
    sourcePath: '/settings',
    targetPath: '/connections',
    milestone: 'evaluationScoreOpened',
    afterTour: 'settings',
    ...onboardingData.transitions.settingsToConnections,
  },
];

const onboardingActionPauses: OnboardingActionPause[] = [
  {
    key: 'agent-builder-action',
    sourcePath: '/agent-builder',
    milestone: 'agentConfigured',
    afterTour: 'agent-builder',
    ...onboardingData.actionPauses.agentBuilder,
  },
  {
    key: 'persona-builder-action',
    sourcePath: '/persona-builder',
    milestone: 'personaConfigured',
    afterTour: 'persona-builder',
    ...onboardingData.actionPauses.personaBuilder,
  },
  {
    key: 'evaluator-builder-action',
    sourcePath: '/evaluator-builder',
    milestone: 'evaluatorConfigured',
    afterTour: 'evaluator-builder',
    ...onboardingData.actionPauses.evaluatorBuilder,
  },
  {
    key: 'conversation-simulation-action',
    sourcePath: '/conversation-simulation',
    milestone: 'simulationRun',
    afterTour: 'conversation-simulation',
    ...onboardingData.actionPauses.conversationSimulation,
  },
  {
    key: 'evaluations-run-action',
    sourcePath: '/evaluations',
    milestone: 'evaluationsRun',
    afterTour: 'evaluations',
    ...onboardingData.actionPauses.evaluationsRun,
  },
  {
    key: 'evaluation-score-open-action',
    sourcePath: '/evaluations',
    milestone: 'evaluationScoreOpened',
    afterMilestones: ['evaluationsRun'],
    ...onboardingData.actionPauses.evaluationScoreOpened,
  },
  // The prompt improver and the export are shown with tooltips in the
  // evaluation-results tour rather than required. Requiring them made the
  // tour long, and the improver button never renders for a connected agent
  // or a perfect score, which left the tour waiting for ever.
];

const actionPausePreferredSelectors: Partial<Record<string, string>> = {
  'agent-builder-action': "[data-onboarding='agent-choice']",
  'persona-builder-action': "[data-onboarding='persona-choice']",
  'evaluator-builder-action': "[data-onboarding='evaluator-choice']",
};

function getBasePath(pathname: string) {
  const matchingPath = Object.keys(routeTourMap).find(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  );
  return matchingPath ?? pathname;
}

function getStepsForTour(tour: OnboardingTourKey): OnboardingStep[] {
  switch (tour) {
    case 'agent-builder':
      return onboardingData.tours.agentBuilder;
    case 'persona-builder':
      return onboardingData.tours.personaBuilder;
    case 'evaluator-builder':
      return onboardingData.tours.evaluatorBuilder;
    case 'conversation-simulation':
      return onboardingData.tours.conversationSimulation;
    case 'conversation-result':
      return onboardingData.tours.conversationResult;
    case 'evaluations':
      return onboardingData.tours.evaluations;
    case 'evaluation-results':
      return onboardingData.tours.evaluationResults;
    case 'evaluation-iteration':
      return onboardingData.tours.evaluationIteration;
    case 'closing':
      return [onboardingData.actionPauses.closing];
    case 'settings':
      return onboardingData.tours.settings;
    case 'connections':
      return onboardingData.tours.connections;
  }
}

function getTransitionByKey(key: OnboardingTransitionKey) {
  return onboardingTransitions.find((transition) => transition.key === key);
}

function getPendingTransition({
  basePath,
  completedMilestones,
  completedTours,
  completedTransitions,
}: {
  basePath: string;
  completedMilestones: Partial<Record<OnboardingMilestone, boolean>>;
  completedTours: Partial<Record<OnboardingTourKey, boolean>>;
  completedTransitions: Partial<Record<OnboardingTransitionKey, boolean>>;
}) {
  return onboardingTransitions.find(
    (transition) =>
      transition.sourcePath === basePath &&
      completedMilestones[transition.milestone] &&
      !completedTransitions[transition.key] &&
      (!transition.afterTour || completedTours[transition.afterTour]),
  );
}

function getPendingActionPause({
  basePath,
  completedMilestones,
  completedTours,
}: {
  basePath: string;
  completedMilestones: Partial<Record<OnboardingMilestone, boolean>>;
  completedTours: Partial<Record<OnboardingTourKey, boolean>>;
}) {
  return onboardingActionPauses.find(
    (pause) =>
      pause.sourcePath === basePath &&
      !completedMilestones[pause.milestone] &&
      (!pause.afterTour || completedTours[pause.afterTour]) &&
      (!pause.afterMilestones ||
        pause.afterMilestones.every(
          (milestone) => completedMilestones[milestone],
        )),
  );
}

/**
 * The page the guide continues on: the first stop whose way out isn't done
 * yet. A stop's tour, action and transition all live on its own page, so
 * resuming there always has something to show.
 */
function getResumePath(
  completedTransitions: Partial<Record<OnboardingTransitionKey, boolean>>,
) {
  const nextTransition = onboardingTransitions.find(
    (transition) => !completedTransitions[transition.key],
  );
  return nextTransition?.sourcePath ?? '/connections';
}

const STAGE_ATTRIBUTE = 'data-onboarding-stage';

function getActionPauseStage(pause: OnboardingActionPause) {
  if (!pause.stages?.length) return undefined;
  const stageName = document
    .querySelector(pause.selector)
    ?.getAttribute(STAGE_ATTRIBUTE);
  return pause.stages.find((stage) => stage.stage === stageName);
}

function getActionPauseElement(pause: OnboardingActionPause) {
  const stage = getActionPauseStage(pause);
  if (stage) return document.querySelector(stage.selector);
  const preferredSelector = actionPausePreferredSelectors[pause.key];
  return (
    (preferredSelector ? document.querySelector(preferredSelector) : null) ??
    document.querySelector(pause.selector)
  );
}

function getActionPausePopoverPlacement(pause: OnboardingActionPause) {
  if (pause.key === 'evaluation-score-open-action') {
    return {
      side: 'top' as const,
      align: 'center' as const,
    };
  }

  return {
    side: 'right' as const,
    align: 'start' as const,
  };
}

function isSidebarTransition(transition: OnboardingTransition) {
  return (
    transition.selector.includes("data-onboarding='nav-") ||
    transition.selector.includes('data-onboarding="nav-')
  );
}

function getTransitionTargetClass(transition: OnboardingTransition) {
  const [, , onboardingTarget] =
    transition.selector.match(/\[data-onboarding=(['"])([^'"]+)\1\]/) ?? [];

  return onboardingTarget
    ? `${TRANSITION_TARGET_CLASS_PREFIX}${onboardingTarget}`
    : null;
}

/**
 * Steps whose element stays clickable while its tooltip is up: the
 * simulation controls, which the tooltip asks people to use, and the score's
 * sections, which start closed and have to be opened to be read.
 */
const INTERACTIVE_STEP_SELECTORS = new Set([
  '#chat-area',
  "[data-onboarding='simulation-settings']",
  "[data-onboarding='evaluation-criteria']",
  "[data-onboarding='evaluation-success']",
  "[data-onboarding='evaluation-failure']",
]);

function getExistingDriverSteps(steps: OnboardingStep[]): DriveStep[] {
  return steps
    .filter((step) => document.querySelector(step.selector))
    .map((step) => ({
      element: step.selector,
      disableActiveInteraction: INTERACTIVE_STEP_SELECTORS.has(step.selector)
        ? false
        : undefined,
      popover: {
        title: step.title,
        description: step.description,
        side: 'right',
        align: 'start',
      },
    }));
}

function getSimulationWaitElement() {
  return (
    document.querySelector('#chat-area') ??
    document.querySelector("[data-onboarding='simulation-wait']") ??
    document.querySelector("[data-onboarding='simulation-settings']")
  );
}

function renderEndOnboardingButton(
  popover: PopoverDOM,
  onEndOnboarding: () => void,
  options: {
    hideDriverNavigation?: boolean;
    transitionHint?: string;
    transitionHintPosition?: 'beforeQuit' | 'afterQuit';
  } = {},
) {
  popover.footer.style.display = 'flex';
  popover.footerButtons.style.display = 'flex';

  if (options.hideDriverNavigation) {
    popover.previousButton.style.display = 'none';
    popover.nextButton.style.display = 'none';
  }

  [popover.previousButton, popover.nextButton].forEach((button) => {
    if (button.style.display !== 'none') {
      button.style.display = 'inline-flex';
      button.style.alignItems = 'center';
      button.style.justifyContent = 'center';
      button.style.textAlign = 'center';
    }
  });

  let endButton = popover.footerButtons.querySelector<HTMLButtonElement>(
    '.promptsafe-tour-quit-button',
  );

  if (!endButton) {
    endButton = document.createElement('button');
    endButton.type = 'button';
    endButton.className = 'promptsafe-tour-quit-button';
    endButton.textContent = onboardingData.intro.skipAction;
    popover.footerButtons.insertBefore(
      endButton,
      popover.footerButtons.firstChild,
    );
  }

  const existingHint = popover.footerButtons.querySelector<HTMLSpanElement>(
    '.promptsafe-tour-transition-hint',
  );
  if (options.transitionHint) {
    popover.footerButtons.classList.add('promptsafe-tour-footer-with-hint');
    const hint = existingHint ?? document.createElement('span');
    hint.className = 'promptsafe-tour-transition-hint';
    hint.textContent = options.transitionHint;
    if (options.transitionHintPosition === 'afterQuit') {
      popover.footerButtons.insertBefore(hint, endButton.nextSibling);
    } else {
      popover.footerButtons.insertBefore(hint, endButton);
    }
  } else {
    popover.footerButtons.classList.remove('promptsafe-tour-footer-with-hint');
    existingHint?.remove();
  }

  endButton.onclick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onEndOnboarding();
  };
}

export function Tutorial() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const {
    hasSeenIntro,
    isDismissed,
    isActive,
    showResumePrompt,
    showCompletionPrompt,
    hasSeenSimulationWaitTip,
    completedMilestones,
    completedTours,
    completedTransitions,
    onboardingSimulationId,
    startOnboarding,
    resumeOnboarding,
    skipOnboarding,
    dismissResumePrompt,
    completeOnboarding,
    showOnboardingCompletionPrompt,
    markSimulationWaitTipSeen,
    markTourComplete,
    markTransitionComplete,
  } = useTutorialStore();
  const isSimulating = useSimulationStore((state) => state.isSimulating);
  const activeTourRef = useRef<ReturnType<typeof driver> | null>(null);
  const activeTourKeyRef = useRef<OnboardingTourKey | null>(null);
  const pendingTourRef = useRef<string | null>(null);
  const pausedTourRef = useRef<{
    tourKey: OnboardingTourKey;
    stepIndex: number;
  } | null>(null);
  const skipTourCompletionRef = useRef(false);
  const activeTransitionRef = useRef<OnboardingTransitionKey | null>(null);
  const activeTransitionElementRef = useRef<Element | null>(null);
  const activeTransitionTargetClassRef = useRef<string | null>(null);
  const activeTransitionClickCleanupRef = useRef<(() => void) | null>(null);
  const activeActionPauseKeyRef = useRef<string | null>(null);
  const activeActionPauseStageRef = useRef<string | undefined>(undefined);
  const stageObserverRef = useRef<MutationObserver | null>(null);
  const activeWaitTipRef = useRef<ReturnType<typeof driver> | null>(null);
  const quitReturnPointRef = useRef<QuitOnboardingReturnPoint | null>(null);
  const [tourRetryTick, setTourRetryTick] = useState(0);
  const [showQuitConfirmation, setShowQuitConfirmation] = useState(false);

  const clearInteractivePause = useCallback(() => {
    document.body.classList.remove(INTERACTIVE_PAUSE_CLASS);
  }, []);

  const clearTransitionPulse = useCallback(() => {
    activeTransitionClickCleanupRef.current?.();
    activeTransitionClickCleanupRef.current = null;
    document.body.classList.remove(TRANSITION_INTERACTION_CLASS);
    activeTransitionElementRef.current?.classList.remove(
      TRANSITION_PULSE_CLASS,
    );
    activeTransitionElementRef.current = null;
    activeTransitionTargetClassRef.current = null;
    Array.from(document.body.classList).forEach((className) => {
      if (className.startsWith(TRANSITION_TARGET_CLASS_PREFIX)) {
        document.body.classList.remove(className);
      }
    });
  }, []);

  const basePath = useMemo(
    () => getBasePath(location.pathname),
    [location.pathname],
  );

  const shouldShowIntro =
    !!user &&
    !hasSeenIntro &&
    !isDismissed &&
    !showQuitConfirmation &&
    basePath !== '/auth' &&
    basePath !== '/auth/callback' &&
    basePath !== '/auth/reset';

  const shouldShowResumePrompt =
    !!user &&
    showResumePrompt &&
    !shouldShowIntro &&
    !showQuitConfirmation &&
    basePath !== '/auth' &&
    basePath !== '/auth/callback' &&
    basePath !== '/auth/reset';

  const shouldShowCompletionPrompt =
    !!user &&
    showCompletionPrompt &&
    !shouldShowIntro &&
    !shouldShowResumePrompt &&
    !showQuitConfirmation &&
    basePath !== '/auth' &&
    basePath !== '/auth/callback' &&
    basePath !== '/auth/reset';

  const startGuidedFlow = () => {
    pausedTourRef.current = null;
    pendingTourRef.current = null;
    activeTransitionRef.current = null;
    activeActionPauseKeyRef.current = null;
    activeActionPauseStageRef.current = undefined;
    stageObserverRef.current?.disconnect();
    stageObserverRef.current = null;
    activeWaitTipRef.current = null;
    activeTourRef.current?.destroy();
    activeTourRef.current = null;
    activeTourKeyRef.current = null;
    clearInteractivePause();
    clearTransitionPulse();
    startOnboarding();
    navigate('/agent-builder');
  };

  const retryPendingTour = useCallback(() => {
    window.setTimeout(() => {
      setTourRetryTick((tick) => tick + 1);
    }, 300);
  }, []);

  const resumeGuidedFlow = () => {
    resumeOnboarding();
    // Being on a guided page isn't enough: Settings and Connections only
    // have a step once the loop is done, so go where the next step is.
    const resumePath = getResumePath(completedTransitions);
    // Once the simulation has run, the next step is about that conversation:
    // reopen it, since the page without an ID starts a new one.
    const simulationPath =
      resumePath === '/conversation-simulation' &&
      completedMilestones.simulationRun &&
      onboardingSimulationId
        ? `${resumePath}/${onboardingSimulationId}`
        : null;
    if (simulationPath) {
      if (location.pathname !== simulationPath) navigate(simulationPath);
    } else if (basePath !== resumePath) {
      navigate(resumePath);
    }
  };

  const requestEndOnboarding = useCallback(
    (returnPoint?: QuitOnboardingReturnPoint) => {
      const activeTour = activeTourRef.current;
      const activeTourKey = activeTourKeyRef.current;
      const resolvedReturnPoint =
        returnPoint ??
        (activeWaitTipRef.current && activeTour === activeWaitTipRef.current
          ? { kind: 'wait' as const }
          : activeTour && activeTourKey
            ? {
                kind: 'tour' as const,
                tourKey: activeTourKey,
                stepIndex: activeTour.getActiveIndex() ?? 0,
              }
            : activeTransitionRef.current
              ? { kind: 'transition' as const }
              : activeTour
                ? { kind: 'action' as const }
                : { kind: 'intro' as const });

      quitReturnPointRef.current = resolvedReturnPoint;

      if (resolvedReturnPoint.kind === 'tour') {
        pausedTourRef.current = {
          tourKey: resolvedReturnPoint.tourKey,
          stepIndex: resolvedReturnPoint.stepIndex,
        };
        skipTourCompletionRef.current = !!activeTour;
      }

      setShowQuitConfirmation(true);
    },
    [],
  );

  const confirmEndOnboarding = useCallback(() => {
    const returnPoint = quitReturnPointRef.current;
    const activeTour = activeTourRef.current;

    if (returnPoint?.kind === 'tour' && activeTour) {
      skipTourCompletionRef.current = true;
    }

    quitReturnPointRef.current = null;
    pausedTourRef.current = null;
    pendingTourRef.current = null;
    activeTransitionRef.current = null;
    activeActionPauseKeyRef.current = null;
    activeActionPauseStageRef.current = undefined;
    stageObserverRef.current?.disconnect();
    stageObserverRef.current = null;
    activeWaitTipRef.current = null;
    activeTourRef.current = null;
    activeTourKeyRef.current = null;
    clearInteractivePause();
    clearTransitionPulse();
    skipOnboarding();
    activeTour?.destroy();
    setShowQuitConfirmation(false);
  }, [clearInteractivePause, clearTransitionPulse, skipOnboarding]);

  const closeCompletionPrompt = () => {
    completeOnboarding();
  };

  const openSettingsFromCompletion = () => {
    completeOnboarding();
    navigate('/settings');
  };

  const showSimulationWaitTip = useCallback(
    (waitElement: Element) => {
      const activeTour = activeTourRef.current;
      if (activeTour) {
        skipTourCompletionRef.current = !!activeTourKeyRef.current;
        activeTourRef.current = null;
        activeTourKeyRef.current = null;
        activeTour.destroy();
      }

      clearTransitionPulse();
      pendingTourRef.current = null;
      activeTransitionRef.current = null;

      let waitResizeObserver: ResizeObserver | null = null;
      let waitRefreshFrame = 0;
      let driverObj: ReturnType<typeof driver>;

      const refreshWaitStage = () => {
        window.cancelAnimationFrame(waitRefreshFrame);
        waitRefreshFrame = window.requestAnimationFrame(() => {
          driverObj.refresh();
        });
      };

      driverObj = driver({
        allowClose: false,
        allowKeyboardControl: false,
        overlayClickBehavior: () => {},
        stagePadding: 8,
        stageRadius: 8,
        overlayOpacity: 0.5,
        popoverClass: 'promptsafe-tour-popover',
        onPopoverRender: (popover) => {
          renderEndOnboardingButton(
            popover,
            () => requestEndOnboarding({ kind: 'wait' }),
            {
              hideDriverNavigation: true,
            },
          );
        },
        onDestroyed: () => {
          waitResizeObserver?.disconnect();
          window.cancelAnimationFrame(waitRefreshFrame);
        },
      });

      activeTourRef.current = driverObj;
      activeTourKeyRef.current = null;
      activeWaitTipRef.current = driverObj;
      markSimulationWaitTipSeen();
      driverObj.highlight({
        element: waitElement,
        popover: {
          title: onboardingData.intro.simulationWaitTitle,
          description: onboardingData.intro.simulationWaitDescription,
          side: 'right',
          align: 'start',
          showButtons: [],
        },
      });

      if ('ResizeObserver' in window) {
        waitResizeObserver = new ResizeObserver(refreshWaitStage);
        waitResizeObserver.observe(waitElement);
      }

      window.setTimeout(refreshWaitStage, 100);
      window.setTimeout(refreshWaitStage, 500);
    },
    [clearTransitionPulse, markSimulationWaitTipSeen, requestEndOnboarding],
  );

  const keepOnboarding = useCallback(() => {
    quitReturnPointRef.current = null;
    setShowQuitConfirmation(false);
  }, []);

  useEffect(() => {
    if (
      !isActive ||
      isDismissed ||
      showQuitConfirmation ||
      hasSeenSimulationWaitTip ||
      !isSimulating ||
      basePath !== '/conversation-simulation'
    ) {
      return;
    }

    const waitElement = getSimulationWaitElement();
    if (!waitElement) return;

    showSimulationWaitTip(waitElement);
  }, [
    basePath,
    hasSeenSimulationWaitTip,
    isActive,
    isDismissed,
    isSimulating,
    showQuitConfirmation,
    showSimulationWaitTip,
  ]);

  useEffect(() => {
    if (isSimulating || !activeWaitTipRef.current) return;

    const activeWaitTip = activeWaitTipRef.current;
    activeWaitTip.destroy();
    if (activeTourRef.current === activeWaitTip) {
      activeTourRef.current = null;
    }
    activeWaitTipRef.current = null;
  }, [isSimulating]);

  useEffect(() => {
    if (!isActive || isDismissed || showQuitConfirmation) return;

    const activeTransitionKey = activeTransitionRef.current;
    if (activeTransitionKey) {
      const activeTransition = getTransitionByKey(activeTransitionKey);
      if (activeTransition?.targetPath === basePath) {
        markTransitionComplete(activeTransitionKey);
        activeTransitionRef.current = null;
        clearTransitionPulse();
        activeTourRef.current?.destroy();
        activeTourRef.current = null;
        return;
      } else {
        return;
      }
    }

    if (basePath === '/conversation-simulation' && isSimulating) return;

    const pendingTransition = getPendingTransition({
      basePath,
      completedMilestones,
      completedTours,
      completedTransitions,
    });

    const pendingActionPause = pendingTransition
      ? undefined
      : getPendingActionPause({
          basePath,
          completedMilestones,
          completedTours,
        });

    if (
      pendingTransition &&
      activeTourRef.current &&
      document.body.classList.contains(INTERACTIVE_PAUSE_CLASS)
    ) {
      activeTourRef.current.destroy();
      activeTourRef.current = null;
      activeTourKeyRef.current = null;
      activeActionPauseKeyRef.current = null;
      activeActionPauseStageRef.current = undefined;
      stageObserverRef.current?.disconnect();
      stageObserverRef.current = null;
      clearInteractivePause();
    }

    if (
      activeActionPauseKeyRef.current &&
      (!pendingActionPause ||
        activeActionPauseKeyRef.current !== pendingActionPause.key)
    ) {
      activeTourRef.current?.destroy();
      activeTourRef.current = null;
      activeTourKeyRef.current = null;
      activeActionPauseKeyRef.current = null;
      activeActionPauseStageRef.current = undefined;
      stageObserverRef.current?.disconnect();
      stageObserverRef.current = null;
      clearInteractivePause();
    }

    const baseTour = routeTourMap[basePath];
    let tourKey: OnboardingTourKey | undefined =
      baseTour === 'conversation-simulation' &&
      completedMilestones.simulationRun
        ? 'conversation-result'
        : baseTour;

    // The last two stops only make sense after the loop has closed.
    if (basePath === '/settings' && !completedTours.closing) {
      tourKey = undefined;
    }
    if (basePath === '/connections' && !completedTours.settings) {
      tourKey = undefined;
    }

    if (basePath === '/evaluations') {
      if (completedTours['evaluation-results'] && !completedTours.closing) {
        tourKey = 'closing';
      } else if (
        completedMilestones.evaluationScoreOpened &&
        !completedTours['evaluation-results']
      ) {
        tourKey = 'evaluation-results';
      }
    }

    if (
      !pendingTransition &&
      !pendingActionPause &&
      (!tourKey || completedTours[tourKey])
    ) {
      return;
    }

    if (
      activeTourRef.current &&
      !pendingTransition &&
      ((activeTourKeyRef.current &&
        tourKey &&
        activeTourKeyRef.current === tourKey &&
        !pendingActionPause) ||
        (!activeTourKeyRef.current &&
          pendingActionPause &&
          activeActionPauseKeyRef.current === pendingActionPause.key &&
          activeActionPauseStageRef.current ===
            getActionPauseStage(pendingActionPause)?.stage))
    ) {
      return;
    }

    const pendingStage = pendingActionPause
      ? getActionPauseStage(pendingActionPause)?.stage
      : undefined;
    const scheduledKey = pendingTransition
      ? `transition:${pendingTransition.key}:${location.pathname}:${location.search}`
      : pendingActionPause
        ? `action:${pendingActionPause.key}:${pendingStage ?? ''}:${location.pathname}:${location.search}`
        : `tour:${tourKey}:${location.pathname}:${location.search}`;
    if (pendingTourRef.current === scheduledKey) return;
    pendingTourRef.current = scheduledKey;

    const timeoutId = window.setTimeout(() => {
      pendingTourRef.current = null;
      if (pendingTransition) {
        const transitionElement = document.querySelector(
          pendingTransition.selector,
        );
        if (!transitionElement) {
          retryPendingTour();
          return;
        }

        activeTourRef.current?.destroy();
        clearTransitionPulse();
        document.body.classList.add(TRANSITION_INTERACTION_CLASS);
        const transitionTargetClass =
          getTransitionTargetClass(pendingTransition);
        if (transitionTargetClass) {
          document.body.classList.add(transitionTargetClass);
          activeTransitionTargetClassRef.current = transitionTargetClass;
        }
        transitionElement.classList.add(TRANSITION_PULSE_CLASS);
        activeTransitionElementRef.current = transitionElement;
        if (isSidebarTransition(pendingTransition)) {
          let hasNavigated = false;
          const navigateFromTransitionClick = (event: Event) => {
            if (!(event instanceof MouseEvent)) return;
            const currentTransitionElement =
              document.querySelector(pendingTransition.selector) ??
              transitionElement;
            const rect = currentTransitionElement.getBoundingClientRect();
            const clickedTransitionTarget =
              event.clientX >= rect.left &&
              event.clientX <= rect.right &&
              event.clientY >= rect.top &&
              event.clientY <= rect.bottom;

            if (!clickedTransitionTarget) return;

            event.preventDefault();
            event.stopPropagation();
            if (!hasNavigated) {
              hasNavigated = true;
              navigate(pendingTransition.targetPath);
            }
          };
          document.addEventListener(
            'pointerdown',
            navigateFromTransitionClick,
            {
              capture: true,
            },
          );
          document.addEventListener('click', navigateFromTransitionClick, true);
          activeTransitionClickCleanupRef.current = () => {
            document.removeEventListener(
              'pointerdown',
              navigateFromTransitionClick,
              {
                capture: true,
              },
            );
            document.removeEventListener(
              'click',
              navigateFromTransitionClick,
              true,
            );
          };
        }
        const driverObj = driver({
          allowClose: false,
          allowKeyboardControl: false,
          disableActiveInteraction: false,
          overlayClickBehavior: () => {},
          stagePadding: 8,
          stageRadius: 8,
          overlayOpacity: 0.5,
          popoverClass: 'promptsafe-tour-popover',
          onPopoverRender: (popover) => {
            const transitionHint =
              pendingTransition.key === 'simulation-to-evaluations'
                ? 'Click the highlighted button →'
                : '← Click the highlighted button';
            renderEndOnboardingButton(
              popover,
              () => requestEndOnboarding({ kind: 'transition' }),
              {
                hideDriverNavigation: true,
                transitionHint,
                transitionHintPosition:
                  pendingTransition.key === 'simulation-to-evaluations'
                    ? 'afterQuit'
                    : 'beforeQuit',
              },
            );
          },
        });
        activeTourRef.current = driverObj;
        activeTourKeyRef.current = null;
        activeTransitionRef.current = pendingTransition.key;
        driverObj.highlight({
          element: transitionElement,
          popover: {
            title: pendingTransition.title,
            description: pendingTransition.description,
            side: 'right',
            align: 'start',
            showButtons: [],
          },
        });
        return;
      }

      if (pendingActionPause) {
        const pauseElement = getActionPauseElement(pendingActionPause);
        if (!pauseElement) {
          retryPendingTour();
          return;
        }
        const pauseStage = getActionPauseStage(pendingActionPause);
        const pauseCopy = pauseStage ?? pendingActionPause;

        activeTourRef.current?.destroy();
        clearTransitionPulse();
        clearInteractivePause();
        document.body.classList.add(INTERACTIVE_PAUSE_CLASS);

        // A staged pause re-highlights when the stage attribute changes.
        stageObserverRef.current?.disconnect();
        stageObserverRef.current = null;
        if (pendingActionPause.stages?.length) {
          const stagedElement = document.querySelector(
            pendingActionPause.selector,
          );
          if (stagedElement) {
            const observer = new MutationObserver(() => retryPendingTour());
            observer.observe(stagedElement, {
              attributes: true,
              attributeFilter: [STAGE_ATTRIBUTE],
            });
            stageObserverRef.current = observer;
          }
        }

        let actionDriver: ReturnType<typeof driver> | null = null;
        const driverObj = driver({
          allowClose: false,
          allowKeyboardControl: false,
          overlayClickBehavior: () => {},
          stagePadding: 8,
          stageRadius: 8,
          overlayOpacity: 0.5,
          popoverClass: 'promptsafe-tour-popover',
          onPopoverRender: (popover) => {
            renderEndOnboardingButton(
              popover,
              () => requestEndOnboarding({ kind: 'action' }),
              {
                hideDriverNavigation: true,
                transitionHint: pauseCopy.hint,
              },
            );
          },
          onDestroyed: () => {
            clearInteractivePause();
            if (activeTourRef.current === actionDriver) {
              activeTourRef.current = null;
              activeActionPauseKeyRef.current = null;
              activeActionPauseStageRef.current = undefined;
              stageObserverRef.current?.disconnect();
              stageObserverRef.current = null;
            }
          },
        });

        actionDriver = driverObj;
        activeTourRef.current = driverObj;
        activeTourKeyRef.current = null;
        activeActionPauseKeyRef.current = pendingActionPause.key;
        activeActionPauseStageRef.current = pauseStage?.stage;
        const popoverPlacement =
          getActionPausePopoverPlacement(pendingActionPause);
        driverObj.highlight({
          element: pauseElement,
          popover: {
            title: pauseCopy.title,
            description: pauseCopy.description,
            side: popoverPlacement.side,
            align: popoverPlacement.align,
            showButtons: [],
          },
        });
        return;
      }

      if (!tourKey) return;
      const steps = getExistingDriverSteps(getStepsForTour(tourKey));
      if (steps.length === 0) {
        retryPendingTour();
        return;
      }
      activeTourRef.current?.destroy();
      clearInteractivePause();
      let driverObj: ReturnType<typeof driver>;
      driverObj = driver({
        steps,
        showProgress: true,
        allowClose: false,
        disableActiveInteraction: true,
        overlayClickBehavior: () => {},
        stagePadding: 8,
        stageRadius: 8,
        overlayOpacity: 0.5,
        popoverClass: 'promptsafe-tour-popover',
        showButtons: ['next'],
        nextBtnText: 'Next',
        doneBtnText: 'Next',
        onPopoverRender: (popover) => {
          renderEndOnboardingButton(popover, () =>
            requestEndOnboarding({
              kind: 'tour',
              tourKey,
              stepIndex: driverObj.getActiveIndex() ?? 0,
            }),
          );
        },
        onDestroyed: () => {
          if (skipTourCompletionRef.current) {
            skipTourCompletionRef.current = false;
            activeTourRef.current = null;
            activeTourKeyRef.current = null;
            return;
          }

          markTourComplete(tourKey);
          if (tourKey === FINAL_ONBOARDING_TOUR) {
            showOnboardingCompletionPrompt();
          }
          clearInteractivePause();
          activeTourRef.current = null;
          activeTourKeyRef.current = null;
        },
      });
      activeTourRef.current = driverObj;
      activeTourKeyRef.current = tourKey;
      const pausedTour = pausedTourRef.current;
      const startIndex =
        pausedTour?.tourKey === tourKey ? pausedTour.stepIndex : undefined;
      pausedTourRef.current = null;
      driverObj.drive(startIndex);
    }, 650);

    return () => {
      window.clearTimeout(timeoutId);
      // Cancelled before it ran: the next run has to schedule it again.
      if (pendingTourRef.current === scheduledKey) {
        pendingTourRef.current = null;
      }
    };
  }, [
    basePath,
    completedMilestones.simulationRun,
    completedMilestones.agentConfigured,
    completedMilestones.evaluatorConfigured,
    completedMilestones.evaluationOpened,
    completedMilestones.evaluationExported,
    completedMilestones.evaluationScoreOpened,
    completedMilestones.evaluationsRun,
    completedMilestones.improvementSuggestionsRequested,
    completedMilestones.personaConfigured,
    completedTransitions,
    completedTours,
    clearInteractivePause,
    isActive,
    isDismissed,
    isSimulating,
    location.pathname,
    location.search,
    clearTransitionPulse,
    markTourComplete,
    markTransitionComplete,
    navigate,
    requestEndOnboarding,
    retryPendingTour,
    showQuitConfirmation,
    showOnboardingCompletionPrompt,
    tourRetryTick,
  ]);

  useEffect(() => {
    return () => {
      clearInteractivePause();
      clearTransitionPulse();
      activeWaitTipRef.current?.destroy();
      activeTourRef.current?.destroy();
    };
  }, [clearInteractivePause, clearTransitionPulse]);

  return (
    <>
      <Dialog open={shouldShowIntro} onOpenChange={() => undefined}>
        <DialogContent
          className="max-h-[90vh] max-w-4xl overflow-y-auto bg-prompt-safecomwhite p-0"
          showCloseButton={false}
          onEscapeKeyDown={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
        >
          <div className="grid min-[560px]:grid-cols-[minmax(240px,0.7fr)_minmax(320px,1.3fr)]">
            <section className="flex flex-col justify-between gap-8 p-6 min-[560px]:min-h-[520px] sm:p-8 md:p-10">
              <DialogHeader className="space-y-4 pr-6 text-left">
                <DialogTitle className="max-w-md text-3xl leading-tight text-prompt-safecomvulcan">
                  {onboardingData.intro.title}
                </DialogTitle>
                <DialogDescription className="max-w-lg text-base leading-relaxed text-prompt-safecomabbey">
                  {onboardingData.intro.description}
                </DialogDescription>
              </DialogHeader>

              <div className="flex flex-col gap-3">
                <Button
                  type="button"
                  className="hover:bg-prompt-safecomoutrageous-orange/90 w-fit bg-prompt-safecomoutrageous-orange text-white"
                  onClick={startGuidedFlow}
                >
                  {onboardingData.intro.startAction}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-fit rounded-md border border-transparent px-3 text-prompt-safecomabbey hover:border-[#00000020] hover:bg-prompt-safecomgallery hover:text-prompt-safecomvulcan"
                  onClick={() => requestEndOnboarding({ kind: 'intro' })}
                >
                  {onboardingData.intro.skipAction}
                </Button>
              </div>
            </section>

            <aside className="border-t border-[#00000014] bg-prompt-safecomfantasy p-5 min-[560px]:border-l min-[560px]:border-t-0 min-[560px]:pt-12">
              <div className="flex flex-col">
                {onboardingData.intro.flow.map((item, index) => {
                  const Icon = FLOW_ICONS[index] ?? Bot;
                  const isLast = index === onboardingData.intro.flow.length - 1;

                  return (
                    <div key={item.title} className="flex flex-col">
                      <div className="rounded-lg border border-[#00000020] bg-prompt-safecomwhite p-3 shadow-sm">
                        <div className="flex items-start gap-3">
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-prompt-safecomoutrageous-orange text-white">
                            <Icon className="h-5 w-5" />
                          </div>
                          <div className="min-w-0">
                            <h3 className="text-sm font-semibold text-prompt-safecomvulcan">
                              {item.title}
                            </h3>
                            <p className="mt-1 text-xs leading-relaxed text-prompt-safecomabbey">
                              {item.description}
                            </p>
                          </div>
                        </div>
                      </div>

                      {!isLast && (
                        <div className="flex justify-center py-2 text-prompt-safecomoutrageous-orange">
                          <ArrowDown className="h-5 w-5" aria-hidden="true" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </aside>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showQuitConfirmation} onOpenChange={() => undefined}>
        <DialogContent
          className="promptsafe-quit-confirmation z-[1000000002] bg-prompt-safecomwhite"
          showCloseButton={false}
          onEscapeKeyDown={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="text-prompt-safecomvulcan">
              Quit onboarding?
            </DialogTitle>
            <DialogDescription className="text-prompt-safecomabbey">
              You can restart the guide from Settings. Your workspace content
              will stay unchanged.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="border-[#00000020] bg-prompt-safecomgallery text-prompt-safecomabbey hover:bg-[#e5e7eb] hover:text-prompt-safecomvulcan"
              onClick={confirmEndOnboarding}
            >
              Quit onboarding
            </Button>
            <Button
              type="button"
              className="hover:bg-prompt-safecomoutrageous-orange/90 bg-prompt-safecomoutrageous-orange text-white"
              onClick={keepOnboarding}
            >
              Keep going
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={shouldShowResumePrompt} onOpenChange={() => undefined}>
        <DialogContent
          className="bg-prompt-safecomwhite"
          showCloseButton={false}
          onEscapeKeyDown={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="text-prompt-safecomvulcan">
              {onboardingData.intro.resumeTitle}
            </DialogTitle>
            <DialogDescription className="text-prompt-safecomabbey">
              {onboardingData.intro.resumeDescription}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={dismissResumePrompt}
            >
              {onboardingData.intro.closeAction}
            </Button>
            <Button
              type="button"
              className="hover:bg-prompt-safecomoutrageous-orange/90 bg-prompt-safecomoutrageous-orange text-white"
              onClick={resumeGuidedFlow}
            >
              {onboardingData.intro.resumeAction}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={shouldShowCompletionPrompt} onOpenChange={() => undefined}>
        <DialogContent
          className="bg-prompt-safecomwhite"
          showCloseButton={false}
          onEscapeKeyDown={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="text-prompt-safecomvulcan">
              {onboardingData.intro.completionTitle}
            </DialogTitle>
            <DialogDescription className="text-prompt-safecomabbey">
              {onboardingData.intro.completionDescription}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={closeCompletionPrompt}
            >
              {onboardingData.intro.completionCloseAction}
            </Button>
            <Button
              type="button"
              className="hover:bg-prompt-safecomoutrageous-orange/90 bg-prompt-safecomoutrageous-orange text-white"
              onClick={openSettingsFromCompletion}
            >
              {onboardingData.intro.completionSettingsAction}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
